import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { requestAssignment } from '../api'
import { IDatasetIntegrationExecution, IDatasetIntegrationExecutions } from '../types'
import { outboundIntegrationAssignmentHasErrors } from '../utils'

/**
 * Group Assignment Executions by Integration Assignment ID
 * - This grouping enables us to easily determine if an assignment has errors.
 * - Provides a central place to get the status of an outbound integration,
 * displaying the total number of successful assignments.
 * - Provides a central place to determine if an assignment has errors.
 */
function selectGroupExecutionByIntegrationAssignmentId(data: IDatasetIntegrationExecutions) {
  return Object.entries(data).reduce<Record<number, IDatasetIntegrationExecution[]>>(
    (acc, [_submissionId, value]) => {
      acc[value.integration_assignment_id] = [
        ...(acc[value.integration_assignment_id] ?? []),
        value,
      ]
      return acc
    },
    {},
  )
}

export const useAssignmentsExecution = (datasetSubmissionId: number) => {
  const { data: assignmentsExecution, isInitialLoading: isLoadingAssignmentExecution } = useQuery({
    queryKey: ['integration-assignmnents', datasetSubmissionId, 'executions'],
    queryFn: () => requestAssignment(datasetSubmissionId),
    select: selectGroupExecutionByIntegrationAssignmentId,
    enabled: true,
    staleTime: 10 * (60 * 1000), // 10 min stale time.
  })

  const assignmentsContainErrors = useCallback(
    integrationAssignmentId => {
      const found = assignmentsExecution?.[integrationAssignmentId]
      return found?.some(x => Object.keys(x).includes('error_trace'))
    },
    [assignmentsExecution],
  )

  const getOutboundStatus = useCallback(
    integrationAssignments => {
      const integrationAssignmentStatus = integrationAssignments
        ?.map(assignment => {
          const hasExecutionErrors = assignmentsContainErrors(assignment.id)
          const hasOutboundAssignmentErrors = outboundIntegrationAssignmentHasErrors(assignment)

          if (hasExecutionErrors || hasOutboundAssignmentErrors) {
            return null
          }

          return true
        })
        .filter(v => v)

      return {
        success: integrationAssignmentStatus?.length,
        total: integrationAssignments?.length,
      }
    },
    [assignmentsContainErrors],
  )

  return {
    assignmentsContainErrors,
    assignmentsExecution,
    isLoadingAssignmentExecution,
    getOutboundStatus,
  }
}
